import { useMemo, useState } from 'react';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { SectionHeading } from '@/components/patterns/SectionHeading';
import { TipSection } from '@/components/patterns/TipSection';
import { FeatureCard } from '@/components/patterns/FeatureCard';
import { TipsExpander } from '@/features/inspiration/components/TipsExpander';
import { IdeaCardsExpander } from '@/features/inspiration/components/IdeaCardsExpander';
import { WorldFilter, type WorldFilterValue } from '@/features/inspiration/components/WorldFilter';
import { getCurrentTipTrio, getRemainingTips } from '@/features/inspiration/data/tips';
import { STORY_IDEAS } from '@/features/inspiration/data/story-ideas';
import { FEATURE_DISCOVERY_ITEMS } from '@/features/inspiration/data/feature-discovery';
import { STRINGS } from '@/strings/strings';

/**
 * Inspiration-Tab — SI-Inspiration rebuild 2026-05-11 (Sebastian
 * Gate-1, post-Mai-Launch content refresh).
 *
 * Three sections, top-to-bottom:
 *
 *   1. 💡 Tipps (Variant Y) — 3 expanded TipSection cards from the
 *      rolling 3-day trio (lead = same tip as Start-Tab), followed
 *      by a TipsExpander revealing the remaining 8 tips as
 *      collapsible rows. Heading is plural ("Tipps", not "Tipp der
 *      Woche") per Decision Q1.
 *
 *   2. Story-Ideen — vertical stack of IdeaTile cards (formerly a
 *      horizontal carousel of small tiles, per Decision Q3 Option
 *      D). 19 entries filtered by an in-page WorldFilter (4 chips,
 *      default "Alle"). Tap navigates to /builder/${world} without
 *      ?theme= prefill (Decision 3B).
 *
 *   3. Hast du schon entdeckt? — vertical stack of 10 FeatureCards
 *      (content set lock 2026-05-11).
 *
 * Backend Phase 2J.4 InspirationTip endpoints remain orphan code —
 * PWA renders entirely from hardcoded data arrays per the Mai-Launch
 * + SI-Inspiration content strategy.
 */
export function InspirationRoute() {
  const S = STRINGS.inspiration;
  const trio = getCurrentTipTrio();
  const remaining = getRemainingTips();
  const [worldFilter, setWorldFilter] = useState<WorldFilterValue>('all');

  const filteredIdeas = useMemo(
    () =>
      worldFilter === 'all' ? STORY_IDEAS : STORY_IDEAS.filter((i) => i.world === worldFilter),
    [worldFilter],
  );

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer>
        <div className="flex flex-col gap-5 py-5 md:gap-7 md:pt-10 md:pb-14 desktop:mx-auto desktop:max-w-[768px]">
          <h1
            className="text-base font-semibold leading-4 text-dream-navy"
            data-testid="inspiration-page-heading"
          >
            {S.pageHeading}
          </h1>

          <SectionHeading heading={S.tipsLabel} id="inspiration-tips">
            <div className="flex w-full flex-col gap-3">
              {trio.map((tip) => (
                <TipSection
                  key={tip.id}
                  heading={`${tip.emoji ?? '💡'} ${tip.title}`}
                  body={tip.body}
                  testId={`inspiration-tip-trio-${tip.id}`}
                />
              ))}
              <TipsExpander tips={remaining} testId="inspiration-tips-expander" />
            </div>
          </SectionHeading>

          <SectionHeading heading={S.storyIdeasHeading} id="inspiration-story-ideas">
            <div className="flex w-full flex-col gap-3">
              <WorldFilter
                value={worldFilter}
                onChange={setWorldFilter}
                testId="inspiration-world-filter"
              />
              {/*
                Keyed by worldFilter so React unmounts/remounts the expander
                on filter change — resets the open/collapsed state to its
                initial value. Matches Sebastian's UX intent: "I want all
                of X" intent fulfilled by the filter, expander is for
                browsing the leftover from within a single filter selection.
              */}
              <IdeaCardsExpander
                key={worldFilter}
                cards={filteredIdeas}
                topNVisible={5}
                testId="story-ideas-expander"
              />
            </div>
          </SectionHeading>

          <SectionHeading heading={S.featureDiscoveryHeading} id="inspiration-feature-discovery">
            <div className="flex w-full flex-col gap-3">
              {FEATURE_DISCOVERY_ITEMS.map((item) => (
                <FeatureCard
                  key={item.id}
                  emoji={item.emoji}
                  title={item.title}
                  body={item.body}
                  testId={`feature-card-${item.id}`}
                />
              ))}
            </div>
          </SectionHeading>
        </div>
      </ContentContainer>
    </div>
  );
}
